import { Territory } from '@amzn/coral_com-amazon-musicpapercontractconfigurationservice';

import { ValueGetterParams } from 'ag-grid-community';
import { AcknowledgedStatus } from 'src/constants';
import {
    getPlanGroup,
    getPlanYYYYMMDDUTCDate,
    getPromoDurationDisplayNumber,
    getRateGroup,
    getTerritory
} from 'src/util/PaperPlans/PaperPlanUtils';
import {
    convertTimestampToYYYYMMDDInLocalTimezone,
    convertTimestampToYYYYMMDDInUTCTimezone
} from 'src/util/dateTimeUtil';

import { getCamelStrFromAllCaps, getRateCardStatus } from '../RateCards';
import { getGeneralStatus } from './status';

export const getRateCardHeaderValue = (params: ValueGetterParams) => {
    return params.colDef.editable ? `${params.colDef.headerName} [CPP]` : `${params.colDef.headerName} [PSM*]`;
};

export const getYYYYMMDDStrFromTimestamp = (params: ValueGetterParams) => {
    const timestamp = params.data[params.column.getColId()];
    if (!timestamp) return '';
    return convertTimestampToYYYYMMDDInLocalTimezone(timestamp);
};

export const getYYYYMMDDUTCStrFromTimestamp = (params: ValueGetterParams) => {
    const timestamp = params.data[params.column.getColId()];
    if (!timestamp) return '';
    return convertTimestampToYYYYMMDDInUTCTimezone(timestamp);
};

export const getLastUpdatedYYYYMMDDStrFromTimestamp = (params: ValueGetterParams) => {
    const timestamp = params.data.changedDate || params.data.creationDate || params.data.lastUpdated;
    if (!timestamp) return '';
    return convertTimestampToYYYYMMDDInLocalTimezone(timestamp);
};

export const getLastUpdatedBy = (params: ValueGetterParams) => {
    return params.data.changedBy || params.data.createdBy;
};

export const getStatusFromStartEndDates = (params: ValueGetterParams) => {
    return getGeneralStatus(params.data);
};

export const getStatusForPaperPlan = (params: ValueGetterParams) => {
    return getGeneralStatus(params.data, true);
};

export const getStatusForRateCard = (params: ValueGetterParams) => {
    return getRateCardStatus(params.data);
};

export const getPlanGroupForPaperPlan = (params: ValueGetterParams) => {
    return getPlanGroup(params.data);
};

export const getRateGroupForPaperPlan = (params: ValueGetterParams) => {
    return getRateGroup(params.data);
};

export const getIsPromoPlan = (params: ValueGetterParams) => {
    return !!params.data.isPromotion;
};

export const getIsCompleteValue = (params: ValueGetterParams) => {
    return !params.data.isDraft;
};

export const getPromoDurationDays = (params: ValueGetterParams) => {
    const cellValue = params.data.promoDurationInDays;
    return cellValue ? getPromoDurationDisplayNumber(cellValue) : '';
};

export const getPlanStartEndDate = (params: ValueGetterParams) => {
    const colName = params.column.getColId();
    const planDateStr = params.data[colName];
    if (!planDateStr) return '';

    return getPlanYYYYMMDDUTCDate(planDateStr, colName === 'endDate');
};

export const getPlanTerritory = (params: ValueGetterParams) => {
    return getTerritory(params.data.territory);
};

export const getStatusForAcknowledgedChanges = (params: ValueGetterParams) => {
    return params.data.isAcknowledged ? AcknowledgedStatus.ACKNOWLEDGED : AcknowledgedStatus.NOTACKNOWLEDGED;
};

export const getTerritoryNameById = (territoryMapping: Record<number, Territory>) => {
    return (params: ValueGetterParams) => territoryMapping[params.data.territoryId]?.territoryName || '';
};

export const getTerritoryCodeById = (territoryMapping: Record<number, Territory>) => {
    return (params: ValueGetterParams) => territoryMapping[params.data.territoryId]?.territoryCode || '';
};

export const getProgramTypeCamelStr = (params: ValueGetterParams) => {
    return getCamelStrFromAllCaps(params.data.programType ?? '', 'DE');
};

export const getCustomerTypeCamelStr = (params: ValueGetterParams) => {
    return getCamelStrFromAllCaps(params.data.paperPlanName ?? '');
};
